import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { 
  Receipt, 
  FileText, 
  Package, 
  DollarSign,
  Building2,
  Calendar,
  User,
  Send,
  Download,
  X,
  CheckCircle,
  Clock,
  AlertTriangle,
  Send as SendIcon
} from 'lucide-react';
import { DebitNote } from '@/data/mockData';

interface DebitNotesDetailsProps {
  debitNote: DebitNote;
  onClose: () => void;
  onSendToVendor: (debitNote: DebitNote) => void;
  onDownload: (debitNote: DebitNote) => void;
  onUpdateStatus: (debitNote: DebitNote, status: DebitNote['status']) => void;
}

export const DebitNotesDetails: React.FC<DebitNotesDetailsProps> = ({
  debitNote,
  onClose,
  onSendToVendor,
  onDownload,
  onUpdateStatus
}) => {
  const getStatusVariant = (status: DebitNote['status']) => {
    switch (status) {
      case 'Open':
        return 'secondary';
      case 'Sent to Vendor':
        return 'default';
      case 'Closed':
        return 'outline';
      case 'Disputed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: DebitNote['status']) => {
    switch (status) {
      case 'Open':
        return <Clock className="h-4 w-4" />;
      case 'Sent to Vendor':
        return <SendIcon className="h-4 w-4" />;
      case 'Closed':
        return <CheckCircle className="h-4 w-4" />;
      case 'Disputed':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Receipt className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{debitNote.debitNoteNumber}</h1>
            <p className="text-muted-foreground">Debit Note Details</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Status and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant={getStatusVariant(debitNote.status)} className="flex items-center gap-2">
                {getStatusIcon(debitNote.status)}
                {debitNote.status}
              </Badge>
              
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Debit Amount</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(debitNote.totalAmount)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {debitNote.status === 'Open' && (
                <Button
                  onClick={() => onSendToVendor(debitNote)}
                  className="flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Send to Vendor
                </Button>
              )}
              
              {debitNote.status === 'Sent to Vendor' && (
                <Button
                  variant="outline"
                  onClick={() => onUpdateStatus(debitNote, 'Closed')}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Mark as Closed
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={() => onDownload(debitNote)}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Debit Note Number</p>
              <p className="font-medium">{debitNote.debitNoteNumber}</p>
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground">Linked Document</p>
              <div className="flex items-center gap-2">
                {debitNote.linkedPONumber && (
                  <Badge variant="outline">
                    PO: {debitNote.linkedPONumber}
                  </Badge>
                )}
                {debitNote.linkedInvoiceNumber && (
                  <Badge variant="outline">
                    INV: {debitNote.linkedInvoiceNumber}
                  </Badge>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Vendor</p>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{debitNote.vendorName}</span>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Reason</p>
              <p className="font-medium">{debitNote.reason}</p>
            </div>

            {debitNote.notes && (
              <div>
                <p className="text-sm text-muted-foreground">Notes</p>
                <p className="text-sm bg-muted/50 p-2 rounded">
                  {debitNote.notes}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">{debitNote.createdByName}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(debitNote.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {debitNote.sentToVendorAt && (
              <div>
                <p className="text-sm text-muted-foreground">Sent to Vendor</p>
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm">
                    {new Date(debitNote.sentToVendorAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}

            {debitNote.closedAt && (
              <div>
                <p className="text-sm text-muted-foreground">Closed</p>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm">
                    {new Date(debitNote.closedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
              <p className="text-2xl font-bold text-red-600">
                {debitNote.items.length}
              </p>
              <p className="text-sm text-muted-foreground">Rejected Items</p>
            </div>

            <div className="text-center p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">
                {debitNote.items.reduce((sum, item) => sum + item.rejectedQuantity, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Total Rejected Qty</p>
            </div>

            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(debitNote.totalAmount)}
              </p>
              <p className="text-sm text-muted-foreground">Debit Amount</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rejected Items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-5 w-5" />
            Rejected Items
          </CardTitle>
          <CardDescription>
            {debitNote.items.length} items in this debit note
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>SKU Code</TableHead>
                  <TableHead>Original Qty</TableHead>
                  <TableHead>Rejected Qty</TableHead>
                  <TableHead>Unit Price</TableHead>
                  <TableHead>Total Value</TableHead>
                  <TableHead>Rejection Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {debitNote.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.skuName}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.skuCode}</Badge>
                    </TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>
                      <span className="font-medium text-red-600">
                        {item.rejectedQuantity}
                      </span>
                    </TableCell>
                    <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                    <TableCell>
                      <span className="font-medium text-red-600">
                        {formatCurrency(item.totalPrice)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{item.rejectionReason}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
